import { useState, FC } from 'react';
import { sanitizeHtml } from 'utils';
import { makeStyles } from '@material-ui/core/styles';
import Box from '@material-ui/core/Box';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import Divider from '@material-ui/core/Divider';
import { KeyboardDatePicker } from '@material-ui/pickers';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import { Editor } from 'react-draft-wysiwyg';
import {
  CAN_CHANGE_JOB,
  CAN_EDIT_OPTIONS_JOB_VIEW,
} from 'providers/permissions';
import { ContentState, convertFromHTML, EditorState } from 'draft-js';
import { convertToHTML } from 'draft-convert';
import { useDataProvider, useNotify } from 'react-admin';
import { PermissionsProvider } from 'providers/permissionsProvider';
import { JobContainer } from '../providers/job';
import { useContainer } from 'unstated-next';
import { Show } from 'components/Show';
import { IconButton } from '@material-ui/core';
import InfoDialog from 'components/InfoDialog';
import Checkbox from '@material-ui/core/Checkbox';
import Chip from '@material-ui/core/Chip';
import { PhaseStructureTypeEnum } from '@vatos-pas/common';
import { AddressEditor } from './address-editor';
import { PaymentOptionCell, PaymentOptionsGrid } from './payment-option-cell';

type Props = {
  isVoidLoading: boolean;
};

export const ProjectDetails: FC<Props> = ({ isVoidLoading }) => {
  const dataProvider = useDataProvider();
  const {
    job,
    subdivision,
    startingPhase,
    editAddress,
    hasAddressChanges,
    addressErrors,
    isSavingAddress,
    setAddressField,
    applyParsedPlace,
    updateCoordinates,
    saveAddress,
  } = useContainer(JobContainer);
  const notify = useNotify();
  const { hasPermission } = PermissionsProvider.useContainer();

  const [options, setOptions] = useState(() => {
    return job?.options?.trim()?.length
      ? EditorState.createWithContent(
          ContentState.createFromBlockArray(
            convertFromHTML(job?.options).contentBlocks,
          ),
        )
      : EditorState.createEmpty();
  });
  const [stockingSpecInfo, setStockingSpecInfo] = useState(false);

  const classes = useStyles();

  const handleEditOptions = async () => {
    if (!job?.id) return;

    try {
      const plainText = convertToHTML(options.getCurrentContent());

      const data = {
        options: plainText.trim().length
          ? convertToHTML(options.getCurrentContent())
          : null,
      };

      const params = {
        id: job.id,
        data,
        previousData: job,
      };
      await dataProvider.update('job', params);
      notify('Options Updated!');
    } catch (error) {
      notify(`Error on Update Options`, 'warning');
    }
  };

  const supervisors =
    subdivision?.subdivisionSupervisors?.map(sup => ({
      name: sup.user.firstName + ' ' + sup.user.lastName,
    })) || [];

  // Check if subdivision is DrywallAndPaint
  const isDrywallAndPaint =
    subdivision?.phaseStructureType === PhaseStructureTypeEnum.DrywallAndPaint;

  const canEditJob = hasPermission(CAN_CHANGE_JOB);
  const addressReadOnly = !canEditJob || !!job?.voided;
  const showSaveAddress = canEditJob && !job?.voided;

  return (
    <Box width="100%">
      <Box className={classes.grayBox}>
        <Typography variant="body1" gutterBottom className={classes.bold}>
          Project Details
        </Typography>
        <Box width="100%" display="flex" mt={2}>
          <Box width="50%" paddingRight={3}>
            <Box>
              <TextField
                InputLabelProps={{ shrink: true }}
                label="Builder"
                fullWidth
                disabled
                className={classes.input}
                value={subdivision?.builder?.name}
              />
              <TextField
                label="Subdivision"
                InputLabelProps={{ shrink: true }}
                fullWidth
                disabled
                className={classes.input}
                value={subdivision?.name}
              />
              <Box display="flex" alignItems="center" style={{ gap: '10px' }}>
                <TextField
                  label="Model"
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  disabled
                  className={classes.input}
                  value={job?.model?.name}
                />
                <Show condition={!!job?.model?.stockingSpec}>
                  <IconButton
                    onClick={() =>
                      job?.model?.stockingSpec
                        ? setStockingSpecInfo(true)
                        : undefined
                    }
                  >
                    <InfoOutlinedIcon className={classes.infoWarning} />
                  </IconButton>
                </Show>
              </Box>

              <Box display="flex" alignItems="center">
                <TextField
                  label="Building #"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  disabled
                  className={classes.input}
                  value={job?.building?.number}
                />
                <TextField
                  label="Lot"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  disabled
                  value={job?.lot}
                  className={classes.inputNoMarginRight}
                />
              </Box>
              <TextField
                label="Block"
                fullWidth
                InputLabelProps={{ shrink: true }}
                className={classes.input}
                disabled
                value={job?.block}
              />
              <TextField
                label="Community Phase"
                fullWidth
                InputLabelProps={{ shrink: true }}
                className={classes.input}
                disabled
                value={job?.communityPhase}
              />
              <Box mt={2}>
                <Typography>Supervisors</Typography>
              </Box>
              <Box display="flex" flexWrap="wrap">
                {supervisors.map(item => (
                  <Chip
                    style={{ margin: '5px 5px' }}
                    key={item.name}
                    label={item.name}
                  />
                ))}
              </Box>
            </Box>
          </Box>
          <Box width="50%">
            <Box display="flex" style={{ gap: 16 }}>
              <KeyboardDatePicker
                disableToolbar
                variant="inline"
                onChange={(date: any) => date}
                format="MM/dd/yyyy"
                margin="normal"
                id="date-picker-approval"
                label="Project Approval Date"
                fullWidth
                disabled
                className={classes.inputDate}
                value={job?.dateDue || null}
                KeyboardButtonProps={{
                  'aria-label': 'change date',
                }}
              />
              <KeyboardDatePicker
                disableToolbar
                className={classes.inputDate}
                variant="inline"
                format="MM/dd/yyyy"
                disabled
                onChange={(date: any) => date}
                margin="normal"
                id="date-picker-due"
                label="Project Due Date"
                fullWidth
                value={null}
                KeyboardButtonProps={{
                  'aria-label': 'change date',
                }}
              />
            </Box>
            <TextField
              className={classes.input}
              fullWidth
              InputLabelProps={{ shrink: true }}
              disabled
              value={job?.idNumber}
              label="Job ID #"
            />
            <Box display="flex" style={{ gap: 16 }}>
              <TextField
                label="Total Square Footage"
                fullWidth
                InputLabelProps={{ shrink: true }}
                className={classes.input}
                value={job?.totalSquareFootage || ''}
                disabled
              />
              <TextField
                label="Geofencing Threshold (ft)"
                fullWidth
                InputLabelProps={{ shrink: true }}
                className={classes.input}
                value={job?.geoDistanceTolerance ?? ''}
                disabled
              />
            </Box>
            <TextField
              label="Job Start Phase"
              fullWidth
              InputLabelProps={{ shrink: true }}
              className={classes.input}
              value={
                startingPhase
                  ? `${startingPhase.name}${
                      job?.startingPhaseApproved ? ' (Approved)' : ''
                    }`
                  : ''
              }
              disabled
            />
          </Box>
        </Box>
        <AddressEditor
          values={editAddress}
          onFieldChange={setAddressField}
          onPlaceSelected={applyParsedPlace}
          onCoordinatesChange={updateCoordinates}
          readOnly={addressReadOnly}
          errors={addressErrors}
          jobId={job?.id}
        />
        <Show condition={showSaveAddress}>
          <Box display="flex" justifyContent="flex-end" mt={2}>
            <Button
              variant="contained"
              color="primary"
              size="large"
              disabled={isSavingAddress || !hasAddressChanges}
              onClick={saveAddress}
              data-testid="save-address-button"
            >
              {isSavingAddress ? (
                <CircularProgress color="inherit" size={24} />
              ) : (
                'Save'
              )}
            </Button>
          </Box>
        </Show>
        <Divider className={classes.divider} />
        <Box mt={2}>
          <Box>
            <Typography className={classes.bold}>
              Additional Workflow Options
            </Typography>
          </Box>
          <Box display="flex">
            <Box display="flex" alignItems="center" mr={2}>
              <Checkbox disabled checked={job?.garageFirst} />
              <Typography>Garage First</Typography>
            </Box>
            {isDrywallAndPaint && (
              <>
                <Box display="flex" alignItems="center" mr={2}>
                  <Checkbox disabled checked={job?.hasTrim} />
                  <Typography>Trim</Typography>
                </Box>
                <Box display="flex" alignItems="center">
                  <Checkbox disabled checked={!!job?.hasExteriorPaint} />
                  <Typography>Exterior Paint</Typography>
                  {job?.hasExteriorPaint && (
                    <Box ml={1.5} width={160}>
                      <TextField
                        id="job-exterior-siding-paint-amount"
                        fullWidth
                        label="Exterior Siding/Paint"
                        disabled
                        value={job.exteriorSidingPaintAmount ?? ''}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Box>
                  )}
                </Box>
              </>
            )}
          </Box>
        </Box>
        <Box mt={2}>
          <Box>
            <Typography className={classes.bold}>
              Additional Payment Options
            </Typography>
          </Box>
          <PaymentOptionsGrid>
            <PaymentOptionCell
              label="High Ceiling"
              checked={job?.highCeiling}
              disabled
            />
            <PaymentOptionCell
              label="Screw Inspection"
              checked={job?.screwInspection}
              disabled
            />
            {isDrywallAndPaint && (
              <>
                <PaymentOptionCell
                  label="Paint Interior Options"
                  checked={job?.hasInteriorOptions}
                  disabled
                >
                  <TextField
                    fullWidth
                    label="Interior Option Cost"
                    disabled
                    value={job?.interiorOptionCost || ''}
                    InputLabelProps={{ shrink: true }}
                  />
                </PaymentOptionCell>
                <PaymentOptionCell
                  label="Paint Exterior Options"
                  checked={job?.hasExteriorOptions}
                  disabled
                >
                  <TextField
                    fullWidth
                    label="Exterior Option Cost"
                    disabled
                    value={job?.exteriorOptionCost || ''}
                    InputLabelProps={{ shrink: true }}
                  />
                </PaymentOptionCell>
                <PaymentOptionCell
                  label="Trim Option"
                  checked={!!job?.hasTrimOptions}
                  disabled
                >
                  <TextField
                    id="job-trim-option-cost"
                    fullWidth
                    label="Trim Option Cost"
                    disabled
                    value={job?.trimOptionCost ?? ''}
                    InputLabelProps={{ shrink: true }}
                  />
                </PaymentOptionCell>
              </>
            )}
          </PaymentOptionsGrid>
        </Box>
      </Box>
      <Box className={classes.grayBox}>
        <Typography variant="body1" gutterBottom className={classes.bold}>
          Options
        </Typography>
        <Box>
          <Show
            condition={
              !!job?.options && !hasPermission(CAN_EDIT_OPTIONS_JOB_VIEW)
            }
          >
            {job?.options && (
              <div
                className="preview"
                dangerouslySetInnerHTML={{ __html: sanitizeHtml(job.options) }}
              />
            )}
          </Show>

          <Show condition={hasPermission(CAN_EDIT_OPTIONS_JOB_VIEW)}>
            <Editor
              editorState={options}
              editorClassName={classes.editor}
              toolbar={{
                options: ['inline', 'blockType', 'list'],
                fontSize: {
                  options: [12, 14, 16, 18, 24, 30],
                },
                inline: {
                  options: ['bold', 'italic', 'underline'],
                },
                blockType: {
                  inDropdown: false,
                  options: ['Normal', 'H1', 'H2', 'H3'],
                },
                list: {
                  options: ['unordered', 'ordered'],
                },
              }}
              onEditorStateChange={value => setOptions(value)}
            />
          </Show>
        </Box>

        <Show condition={hasPermission(CAN_CHANGE_JOB)}>
          <Box display="flex" justifyContent="flex-end" mt={2}>
            <Button
              variant="contained"
              size="large"
              className={classes.button}
              disabled={isVoidLoading}
              onClick={handleEditOptions}
            >
              Submit
            </Button>
          </Box>
        </Show>

        <InfoDialog
          title="Stock Specs"
          content={job?.model?.stockingSpec}
          open={stockingSpecInfo}
          handleClose={() => setStockingSpecInfo(false)}
        />
      </Box>
    </Box>
  );
};

const useStyles = makeStyles({
  input: {
    margin: '8px 0px',
  },
  inputDate: {
    margin: '0px 0px 15px',
  },
  inputNoMarginRight: {
    margin: '8px 0px 8px 15px',
  },
  grayBox: {
    border: '1.5px solid lightgray',
    borderRadius: '10px',
    padding: '25px',
    marginBottom: '5px',
  },
  button: {
    marginLeft: 'auto',
    marginRight: 20,
    backgroundColor: '#4caf51',
    color: 'white',
    '&:hover': {
      backgroundColor: '#4caf51',
    },
  },
  bold: {
    fontWeight: 'bold',
  },
  editor: {
    border: '1px solid gray',
    padding: '3px',
  },
  infoWarning: {
    color: '#ff9800',
  },
  divider: {
    width: '100%',
    margin: '24px 0 8px',
  },
});
